import { useEffect, useRef, useState, createContext, createElement } from "react";
import * as signalR from "@microsoft/signalr";
import { useKeycloak } from "@react-keycloak/web";
import { getBaseURL } from "./helpers";

// Create a new context
export const SignalRContext = createContext<signalR.HubConnection | null>(null);

// Create a provider component
export const SignalRProvider = ({ children }: any) => {
    const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
    const { keycloak } = useKeycloak();

    // Guards against React StrictMode / re-render double mounts creating a
    // second connection (which is one of the ways the hub gets flooded).
    const buildingRef = useRef(false);

    useEffect(() => {
        if (!keycloak.authenticated) {
            return;
        }

        // Only one connection per provider lifetime.
        if (buildingRef.current) {
            return;
        }
        buildingRef.current = true;

        const tenantIdentifier = window.location.pathname.split("/")[1];

        const newConnection = new signalR.HubConnectionBuilder()
            .withUrl(
                `${getBaseURL()}/monitoringHub?p365-tenant-url-identifier=${encodeURIComponent(tenantIdentifier)}`,
                {
                    // Force a single WebSocket. Without this SignalR tries WS,
                    // fails behind the proxy, and falls back to Long Polling,
                    // which polls the hub continuously (the request flood).
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.WebSockets,
                    // Called on connect AND on every (re)connect, so the token
                    // stays fresh without us tearing down the connection.
                    accessTokenFactory: async () => {
                        try {
                            await keycloak.updateToken(30);
                        } catch {
                            /* keep last token, reconnect will retry */
                        }
                        if (!keycloak.token) {
                            throw new Error("Not authenticated");
                        }
                        return keycloak.token;
                    },
                }
            )
            .withAutomaticReconnect()
            .build();

        newConnection.onclose((error) => console.log("SignalR connection closed", error));
        newConnection.onreconnecting((error) => console.log("SignalR reconnecting", error));
        newConnection.onreconnected((id) => {
            console.log("SignalR reconnected", id);
            window.dispatchEvent(new CustomEvent("signalr-connected"));
        });

        newConnection
            .start()
            .then(() => {
                console.log("SignalR connection started successfully");
                setConnection(newConnection);
                window.dispatchEvent(new CustomEvent("signalr-connected"));
            })
            .catch((err) => {
                console.error("Connection start failed:", err);
                buildingRef.current = false;
                window.dispatchEvent(
                    new CustomEvent("signalr-connection-failed", { detail: err })
                );
            });

        return () => {
            buildingRef.current = false;
            newConnection.stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keycloak.authenticated]);

    return createElement(SignalRContext.Provider, { value: connection }, children);
};
